<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('inventoryList.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('inventoryList.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="ts-flex ts-items-center ts-space-x-2">
            <a-range-picker
              v-model="dateRange"
              :allowClear="false"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              style="width:200px"
            />
            <ts-button @click.prevent="show_form_refresh = true">
              <i class="fas fa-sync"></i>
            </ts-button>
            <ts-button
              @click.prevent="refreshCurrentStock"
              :waiting="refreshing"
              outline
              color="info"
            >
              {{ $t('inventoryList.refreshCurrentStock') }}
            </ts-button>
            <ts-button
              @click.prevent="show_form_refresh_duration = true"
              outline
              color="primary"
            >
              {{ $t('inventoryList.refreshStockByDuration') }}
            </ts-button>
          </div>
          <div class="d-flex ts-space-x-2 ts-items-center">
            <a-select
              v-model="model.category_id"
              showSearch
              mode="multiple"
              :placeholder="$t('inventoryList.category')"
              :options="product_categories"
              :maxTagTextLength="17"
              :maxTagCount="1"
              style="width:220px"
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
            ></a-select>
            <a-select
              v-model="model.branch_id"
              showSearch
              mode="multiple"
              :placeholder="$t('all')"
              :options="branchList"
              :maxTagTextLength="17"
              :maxTagCount="1"
              style="width:220px"
              @change="onChangeBranch"
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
            ></a-select>
            <a-select
              v-model="model.location_id"
              showSearch
              mode="multiple"
              :placeholder="$t('inventoryList.warehouseFilter')"
              :options="locations"
              :maxTagTextLength="17"
              :maxTagCount="1"
              style="width:220px"
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
            ></a-select>
            <ts-input
              v-model="search"
              v-tooltip="$t('inventoryList.search')"
              :placeholder="$t('search')"
              style="width: 200px;"
              class="inline-block"
            />
            <Button
              :disabled="loading"
              :loading="loading"
              type="primary"
              @click="fetchData"
              >{{ $t('view') }}
            </Button>
          </div>
        </div>
      </ts-card-wrapper>
      <ts-loading-banner :loading="loading">
        <div class="overflow-auto">
          <Table
            :data="resources" 
            :columns="columns" 
            size="small"
            highlight-row
            stripe
            border 
            max-height="700"
            :loading="loading"
          >
              <template slot-scope="{ row }" slot="cost">
                  {{ $defaultCurrencyWithCode(row.cost) }}
              </template>
              <template slot-scope="{ row }" slot="retailPrice">
                  {{ $defaultCurrencyWithCode(row.retail_price) }}
              </template>
              <template slot-scope="{ row }" slot="dealerPrice">
                  {{ $defaultCurrencyWithCode(row.dealer_price) }}
              </template>
              <template slot-scope="{ row }" slot="quantity_in">
                {{ row.quantity_in ? row.quantity_in : '-' }}
              </template>
              <template slot-scope="{ row }" slot="trasnfer_in">
                {{ row.trasnfer_in ? row.trasnfer_in : '-' }}
              </template>
              <template slot-scope="{ row }" slot="adjustment_in">
                {{ row.adjustment_in ? row.adjustment_in : '-' }}
              </template>
              <template slot-scope="{ row }" slot="return_from_vendor_in">
                {{
                  row.return_from_vendor_in
                    ? row.return_from_vendor_in
                    : '-'
                }}
              </template>
              <template slot-scope="{ row }" slot="return_from_customer_in">
                {{
                  row.return_from_customer_in
                    ? row.return_from_customer_in
                    : '-'
                }}
              </template>
              <template slot-scope="{ row }" slot="total_in">
                {{ row.total_in ? row.total_in : '-' }}
              </template>

              <template slot-scope="{ row }" slot="qauntity_out">
                {{ row.qauntity_out ? row.qauntity_out : '-' }}
              </template>
              <template slot-scope="{ row }" slot="transfer_out">
                {{ row.transfer_out ? row.transfer_out : '-' }}
              </template>
              <template slot-scope="{ row }" slot="adjustment_out">
                {{ row.adjustment_out ? row.adjustment_out : '-' }}
              </template>
              <template slot-scope="{ row }" slot="return_to_vendor_out">
                {{
                  row.return_to_vendor_out
                    ? row.return_to_vendor_out
                    : '-'
                }}
              </template>
              <template slot-scope="{ row }" slot="write_off_out">
                {{ row.write_off_out ? row.write_off_out : '-' }}
              </template>
              <template slot-scope="{ row }" slot="sp_replacement_out">
                {{
                  row.sp_replacement_out ? row.sp_replacement_out : '-'
                }}
              </template>
              <template slot-scope="{ row }" slot="service_field_order">
                {{
                  row.service_field_order ? row.service_field_order : '-'
                }}
              </template>
              <template slot-scope="{ row }" slot="total_out">
                {{ row.total_out ? row.total_out : '-' }}
              </template>
          </Table>
        </div>
      </ts-loading-banner>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
    </ts-card>
    <a-modal
      v-model="show_form_refresh"
      :title="$t('inventoryList.refreshStock')"
      width="300px"
      :centered="true"
      @ok="onRefreshStock"
      :confirmLoading="refreshing"
    >
      <a-date-picker
        v-model="model.date"
        format="DD-MM-YYYY"
        valueFormat="DD-MM-YYYY"
        :allowClear="false"
        class="ts-w-full"
      >
      </a-date-picker>
    </a-modal>
    <a-modal
      v-model="show_form_refresh_duration"
      :title="$t('inventoryList.refreshStockByDuration')"
      width="300px"
      :centered="true"
      @ok="onRefreshStockByDuration"
      :confirmLoading="refreshDuration"
    >
    <a-range-picker
      v-model="model.dateRangeDuration"
      :allowClear="false"
      format="DD-MM-YYYY"
      valueFormat="DD-MM-YYYY"
      class="ts-w-full"
    />
    </a-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import moment from 'moment'
export default {
  name: 'inventoryList',
  data() {
    return {
      refreshing: false,
      refreshDuration: false,
      show_form_refresh: false,
      show_form_refresh_duration: false,
      locationLoading: false,
      loading: false,
      product_categories: [],
      branchList: [],
      locations: [],
      search: '',
      model: {
        date: moment().format('DD-MM-YYYY'),
        dateRangeDuration: [moment().add(-7, 'days').format('DD-MM-YYYY'), moment().format('DD-MM-YYYY')],
        category_id: undefined,
        branch_id: undefined,
        location_id: undefined
      }
    }
  },
  created() {
    this.refreshDailyStock()
    this.fetchResource()
    this.fetchCategory()
  },
  computed: {
    ...mapState('stock/inventoryList', ['resources', 'pagination']),
    dateRange: {
      get() {
        return this.$store.state.stock.inventoryList.dateRange
      },
      set(newValue) {
        this.$store.commit('stock/inventoryList/SET_DATE_RANGE', newValue)
      }
    },
    columns () {
      return [
        { 
          title: this.$t('inventoryList.branchName'), 
          width: 140,
          key: 'branch_name_en' 
        },
        { 
          title: this.$t('inventoryList.location'), 
          width: 160,
          key: 'location_name' 
        },
        { 
          title: this.$t('inventoryList.barcode'), 
          width: 110,
          align: 'center',
          key: 'barcode' 
        },
        { 
          title: this.$t('inventoryList.category'), 
          width: 120,
          key: 'category' 
        },
        { 
          title: this.$t('inventoryList.productGroup'), 
          width: 120,
          key: 'product_group' 
        },
        { 
          title: this.$t('inventoryList.description'), 
          width: 320,
          key: 'product_name' 
        },
        { 
          title: this.$t('inventoryList.begin'), 
          width: 90,
          align: 'center',
          key: 'begining_balance' 
        },
        { 
          title: this.$t('inventoryList.cost'),
          slot: 'cost', 
          width: 110,
          align: 'right' 
        },
        {
          title: this.$t('inventoryList.retailPrice'),
          slot: 'retailPrice',
          width: 130,
          align: 'right'
        },
        { 
          title: this.$t('inventoryList.dealerPrice'),
          slot: 'dealerPrice',
          width: 130, 
          align: 'right' 
        },
        {
          title: this.$t('inventoryList.in'),
          align: 'center',
          children: [
            {
              title: this.$t('inventoryList.stock'),
              slot: 'quantity_in',
              align: 'center',
              minWidth: 90
            },
            {
              title: this.$t('inventoryList.transfer'),
              slot: 'trasnfer_in',
              align: 'center',
              minWidth: 88
            },
            {
              title: this.$t('inventoryList.adjustment'),
              slot: 'adjustment_in',
              align: 'center',
              minWidth: 108
            },
            {
              title: this.$t('inventoryList.fromVendor'),
              slot: 'return_from_vendor_in',
              align: 'center',
              minWidth: 116
            },
            {
              title: this.$t('inventoryList.returnCustomer'),
              slot: 'return_from_customer_in',
              align: 'center',
              minWidth: 108
            },
            {
              title: this.$t('inventoryList.total'),
              slot: 'total_in',
              align: 'center',
              minWidth: 90
            },
          ]
        },
        {
          title: this.$t('inventoryList.out'),
          align: 'center',
          children: [
            {
              title: this.$t('inventoryList.sale'),
              slot: 'qauntity_out',
              align: 'center',
              minWidth: 90
            },
            {
              title: this.$t('inventoryList.transfer'),
              slot: 'transfer_out',
              align: 'center',
              minWidth: 88
            },
            {
              title: this.$t('inventoryList.adjustment'),
              slot: 'adjustment_out',
              align: 'center',
              minWidth: 108
            },
            {
              title: this.$t('inventoryList.toVendor'),
              slot: 'return_to_vendor_out',
              align: 'center',
              minWidth: 100
            },
            {
              title: this.$t('inventoryList.writeOff'),
              slot: 'write_off_out',
              align: 'center',
              minWidth: 92
            },
            {
              title: this.$t('inventoryList.spReplacement'),
              slot: 'sp_replacement_out',
              align: 'center',
              minWidth: 134
            },
            {
              title: this.$t('inventoryList.serviceFieldOrder'),
              slot: 'service_field_order',
              align: 'center',
              minWidth: 112
            },
            {
              title: this.$t('inventoryList.total'),
              slot: 'total_out',
              align: 'center',
              minWidth: 90
            },
          ]
        },
        { 
          title: this.$t('inventoryList.ending'), 
          width: 86,
          align: 'center',
          key: 'ending_balance' 
        }
      ]
    }
  },
  methods: {
    ...mapActions('stock/inventoryList', ['getProductCategory']),
    format_number(value) {
      return parseFloat(value).toFixed(2)
    },
    async fetchResource() {
      this.loading = true
      await this.$store
        .dispatch('security/user/getBranchAssigned')
        .then(response => {
          this.branchList = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
          this.model.branch_id = [
            this.$store.state.authUser.user.default_branch.branch_id
          ]
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
          this.onChangeBranch()
        })

      this.loading = false
    },
    onChangeBranch() {
      this.locationLoading = true
      this.locations = []
      this.model.location_id = undefined
      this.$store
        .dispatch('report/resource/getLocation', {
          branch_id: this.model.branch_id
        })
        .then(response => {
          this.locations = response.data.map(el => ({
            value: el.location_id,
            label: el.location_name_en
          }))
        })
        .finally(() => {
          this.locationLoading = false
        })
    },
    fetchCategory(attributes) {
      this.loading = true
      this.$store
        .dispatch('stock/inventoryList/getProductCategory', { ...attributes })
        .then(response => {
          this.product_categories = response.data.map(el => ({
            value: el.category_id,
            label: el.category_name_en
          }))
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('stock/inventoryList/fetch', {
          search: this.search,
          branch_id: _.join(this.model.branch_id, ','),
          location_id: this.model.location_id,
          category_id: this.model.category_id,
          ...attributes
        })
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    onRefreshStock() {
      this.refreshing = true
      this.$store
        .dispatch('stock/inventoryList/refreshStock', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.show_form_refresh = false
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.refreshing = false
        })
    },
    onRefreshStockByDuration() {
      this.refreshDuration = true
      this.$store
        .dispatch('stock/inventoryList/refreshStockByDuration', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.show_form_refresh_duration = false
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.refreshDuration = false
        })
    },
    refreshCurrentStock() {
      this.refreshing = true
      this.$store
        .dispatch('stock/inventoryList/refreshCurrentStock')
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.refreshing = false
        })
    },
    refreshDailyStock() {
      this.refreshing = true
      this.$store
        .dispatch('stock/inventoryList/refreshDailyStock')
        .then(response => {
          // this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.refreshing = false
        })
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchCategory()
    })
  }
}
</script>

<style scoped>
.table th {
  padding: 0.75rem;
  vertical-align: middle !important;
  border-top: 1px solid #d3d3d3;
}
</style>
